// T9 Product Master Enrichment — vendor SKU and per-warehouse GL account hooks.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../../shared/api/axiosInstance';
import type {
  ProductVendorSku,
  ProductVendorSkuCreate,
  ProductVendorSkuUpdate,
  ProductWarehouseAccount,
  ProductWarehouseAccountCreate,
  ProductWarehouseAccountUpdate,
  VendorProductOffering,
} from '../types';
import { productKeys } from './useProducts';

const BASE = '/api/v1/master-data/products';

// ── Vendor SKUs ────────────────────────────────────────────────────────────

export const vendorSkuKeys = {
  all: ['product-vendor-skus'] as const,
  byProduct: (productId: string) => [...vendorSkuKeys.all, productId] as const,
  byVendor: (vendorOrganizationId: string) =>
    [...vendorSkuKeys.all, 'by-vendor', vendorOrganizationId] as const,
};

/**
 * Reciprocal of useProductVendorSkus: the products a given vendor offers.
 * Powers the procurement vendor-profile Products tab.
 */
export function useProductsByVendor(vendorOrganizationId: string) {
  return useQuery({
    queryKey: vendorSkuKeys.byVendor(vendorOrganizationId),
    queryFn: async () => {
      const response = await axiosInstance.get<VendorProductOffering[]>(
        `${BASE}/vendor-skus/by-vendor/${vendorOrganizationId}`,
      );
      return response.data;
    },
    enabled: !!vendorOrganizationId,
  });
}

export function useProductVendorSkus(productId: string) {
  return useQuery({
    queryKey: vendorSkuKeys.byProduct(productId),
    queryFn: async () => {
      const response = await axiosInstance.get<ProductVendorSku[]>(
        `${BASE}/products/${productId}/vendor-skus`,
      );
      return response.data;
    },
    enabled: !!productId,
  });
}

export function useCreateVendorSku() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ productId, data }: { productId: string; data: ProductVendorSkuCreate }) => {
      const response = await axiosInstance.post<{ data: ProductVendorSku }>(
        `${BASE}/products/${productId}/vendor-skus`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: vendorSkuKeys.byProduct(productId) });
    },
  });
}

export function useUpdateVendorSku() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      vendorSkuId,
      productId: _productId,
      data,
    }: {
      vendorSkuId: string;
      productId: string;
      data: ProductVendorSkuUpdate;
    }) => {
      const response = await axiosInstance.put<{ data: ProductVendorSku }>(
        `${BASE}/vendor-skus/${vendorSkuId}`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: vendorSkuKeys.byProduct(productId) });
    },
  });
}

export function useDeleteVendorSku() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      vendorSkuId,
      productId: _productId,
    }: {
      vendorSkuId: string;
      productId: string;
    }) => {
      await axiosInstance.delete(`${BASE}/vendor-skus/${vendorSkuId}`);
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: vendorSkuKeys.byProduct(productId) });
    },
  });
}

// ── Warehouse GL account overrides ─────────────────────────────────────────

export const warehouseAccountKeys = {
  all: ['product-warehouse-accounts'] as const,
  byProduct: (productId: string) => [...warehouseAccountKeys.all, productId] as const,
};

export function useProductWarehouseAccounts(productId: string) {
  return useQuery({
    queryKey: warehouseAccountKeys.byProduct(productId),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductWarehouseAccount[] }>(
        `${BASE}/products/${productId}/warehouse-accounts`,
      );
      return response.data.data;
    },
    enabled: !!productId,
  });
}

export function useCreateWarehouseAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      productId,
      data,
    }: {
      productId: string;
      data: ProductWarehouseAccountCreate;
    }) => {
      const response = await axiosInstance.post<{ data: ProductWarehouseAccount }>(
        `${BASE}/products/${productId}/warehouse-accounts`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: warehouseAccountKeys.byProduct(productId) });
    },
  });
}

export function useUpdateWarehouseAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      accountId,
      productId: _productId,
      data,
    }: {
      accountId: string;
      productId: string;
      data: ProductWarehouseAccountUpdate;
    }) => {
      const response = await axiosInstance.put<{ data: ProductWarehouseAccount }>(
        `${BASE}/warehouse-accounts/${accountId}`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: warehouseAccountKeys.byProduct(productId) });
    },
  });
}

export function useDeleteWarehouseAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      accountId,
      productId: _productId,
    }: {
      accountId: string;
      productId: string;
    }) => {
      await axiosInstance.delete(`${BASE}/warehouse-accounts/${accountId}`);
    },
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: warehouseAccountKeys.byProduct(productId) });
    },
  });
}

// productKeys re-exported for convenience when callers also refresh the product.
export { productKeys };
